import { supabase, supabasePublicAnon } from "./supabase";
import { getMyDjangoUserId } from "./supabaseCommunity";
import {
  extractForeducatorMediaKeysFromHtml,
  foreducatorMediaObjectKey,
  removeForeducatorMediaObjects,
} from "./supabaseStorageCleanup";

const DEFAULT_LIMIT = 12;

export type InformateVibeSiteSort = "latest" | "popular" | "hits";

export interface InformateVibeCategory {
  id: number;
  name: string;
  sort_order: number;
}

export interface InformateVibeTag {
  id: number;
  name: string;
  tag_group: string;
  sort_order: number;
}

export interface InformateVibeSiteItem {
  id: number;
  title: string;
  url: string;
  description: string;
  category_id: number;
  category_name: string;
  preview_image_url: string | null;
  preview_title: string | null;
  author_user_id: number | null;
  author_display_name: string;
  guest_display_name: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  tag_names: string[];
  tag_ids: number[];
  likes_count: number;
  hits: number;
  comments_count: number;
  is_liked: boolean;
  can_edit: boolean;
}

export interface InformateVibeSiteComment {
  id: number;
  site_id: number;
  parent_id: number | null;
  author_user_id: number;
  author_display_name: string;
  author_avatar_path: string | null;
  body: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
}

function mainClient() {
  if (!supabase) throw new Error("Supabase가 설정되지 않았습니다.");
  return supabase;
}

function publicClient() {
  const c = supabasePublicAnon ?? supabase;
  if (!c) throw new Error("Supabase가 설정되지 않았습니다.");
  return c;
}

function mapSiteRow(d: Record<string, unknown>): InformateVibeSiteItem {
  return {
    id: Number(d.id),
    title: String(d.title ?? ""),
    url: String(d.url ?? ""),
    description: String(d.description ?? ""),
    category_id: Number(d.category_id),
    category_name: String(d.category_name ?? ""),
    preview_image_url: d.preview_image_url == null ? null : String(d.preview_image_url),
    preview_title: d.preview_title == null ? null : String(d.preview_title),
    author_user_id: d.author_user_id == null ? null : Number(d.author_user_id),
    author_display_name: String(d.author_display_name ?? "익명"),
    guest_display_name: d.guest_display_name == null ? null : String(d.guest_display_name),
    status: String(d.status ?? "published"),
    created_at: String(d.created_at ?? ""),
    updated_at: String(d.updated_at ?? ""),
    tag_names: Array.isArray(d.tag_names) ? (d.tag_names as string[]) : [],
    tag_ids: Array.isArray(d.tag_ids) ? (d.tag_ids as number[]).map(Number) : [],
    likes_count: Number(d.likes_count ?? 0),
    hits: Number(d.hits ?? 0),
    comments_count: Number(d.comments_count ?? 0),
    is_liked: Boolean(d.is_liked),
    can_edit: Boolean(d.can_edit),
  };
}

/** Collect Storage object keys owned by this site (preview + description HTML images). */
function collectVibeSiteStoragePaths(previewImageUrl: string | null | undefined, description: string | null | undefined): string[] {
  const keys = new Set<string>();
  const previewKey = foreducatorMediaObjectKey(previewImageUrl);
  if (previewKey) keys.add(previewKey);
  for (const key of extractForeducatorMediaKeysFromHtml(description)) {
    keys.add(key);
  }
  return [...keys];
}

/**
 * Load site media fields (including hidden rows) then remove Storage objects.
 * Throws if storage.remove fails — do not pretend success.
 */
async function cleanupVibeSiteStorage(siteId: number): Promise<void> {
  const c = mainClient();
  const { data, error } = await c
    .from("informate_vibe_site")
    .select("preview_image_url, description")
    .eq("id", siteId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return;
  const paths = collectVibeSiteStoragePaths(
    data.preview_image_url as string | null,
    data.description as string | null
  );
  if (paths.length === 0) return;
  await removeForeducatorMediaObjects(paths);
}

export async function listInformateVibeCategoriesFromSupabase(): Promise<InformateVibeCategory[]> {
  const c = publicClient();
  const { data, error } = await c
    .from("informate_vibe_site_category")
    .select("id, name, sort_order")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: Number(r.id),
    name: String(r.name),
    sort_order: Number(r.sort_order),
  }));
}

export async function listInformateVibeTagsFromSupabase(): Promise<InformateVibeTag[]> {
  const c = publicClient();
  const { data, error } = await c
    .from("informate_vibe_site_tag")
    .select("id, name, tag_group, sort_order")
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: Number(r.id),
    name: String(r.name),
    tag_group: String(r.tag_group ?? "기타"),
    sort_order: Number(r.sort_order ?? 100),
  }));
}

export async function listInformateVibeSitesFromSupabase(params: {
  page?: number;
  limit?: number;
  sort?: InformateVibeSiteSort;
  search?: string;
  categoryId?: number | null;
  tagId?: number | null;
}): Promise<{ count: number; next: string | null; results: InformateVibeSiteItem[] }> {
  const page = params.page ?? 1;
  const limit = params.limit ?? DEFAULT_LIMIT;
  // Session-aware: view columns can_edit / is_liked use django_user_id()
  const c = mainClient();

  let q = c.from("informate_vibe_site_list_view").select("*", { count: "exact" });

  const search = (params.search ?? "").trim();
  if (search) {
    q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
  }
  if (params.categoryId) {
    q = q.eq("category_id", params.categoryId);
  }
  if (params.tagId) {
    q = q.contains("tag_ids", [params.tagId]);
  }

  if (params.sort === "hits") {
    q = q.order("hits", { ascending: false }).order("created_at", { ascending: false }).order("id", { ascending: false });
  } else if (params.sort === "popular") {
    q = q.order("likes_count", { ascending: false }).order("created_at", { ascending: false });
  } else {
    q = q.order("created_at", { ascending: false });
  }

  const offset = (page - 1) * limit;
  q = q.range(offset, offset + limit - 1);

  const { data, error, count } = await q;
  if (error) throw error;

  const total = count ?? 0;
  return {
    count: total,
    next: offset + limit < total ? "next" : null,
    results: (data ?? []).map((row) => mapSiteRow(row as Record<string, unknown>)),
  };
}

export async function getInformateVibeSiteFromSupabase(id: number): Promise<InformateVibeSiteItem | null> {
  // Session-aware: view columns can_edit / is_liked use django_user_id()
  const c = mainClient();
  const { data, error } = await c.from("informate_vibe_site_list_view").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return mapSiteRow(data as Record<string, unknown>);
}

export async function fetchVibeSitePreview(url: string): Promise<{
  image: string | null;
  images: string[];
  title: string | null;
  description: string | null;
  siteName: string | null;
}> {
  const res = await fetch("/api/informate/vibe-sites/fetch-preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "미리보기를 가져오지 못했습니다.");
  }
  const data = (await res.json()) as {
    image?: string | null;
    images?: string[];
    title?: string | null;
    description?: string | null;
    siteName?: string | null;
  };
  return {
    image: data.image ?? null,
    images: Array.isArray(data.images) ? data.images : data.image ? [data.image] : [],
    title: data.title ?? null,
    description: data.description ?? null,
    siteName: data.siteName ?? null,
  };
}

export async function createInformateVibeSiteAuth(params: {
  title: string;
  url: string;
  description: string;
  categoryId: number;
  tagIds: number[];
  previewImageUrl?: string | null;
  previewTitle?: string | null;
}): Promise<number> {
  const c = mainClient();
  const { data, error } = await c.rpc("informate_vibe_site_create", {
    p_title: params.title,
    p_url: params.url,
    p_description: params.description,
    p_category_id: params.categoryId,
    p_tag_ids: params.tagIds,
    p_preview_image_url: params.previewImageUrl ?? null,
    p_preview_title: params.previewTitle ?? null,
  });
  if (error) throw error;
  return Number(data);
}

export async function createInformateVibeSiteGuest(params: {
  title: string;
  url: string;
  description: string;
  categoryId: number;
  password: string;
  guestDisplayName?: string;
  tagIds: number[];
  previewImageUrl?: string | null;
  previewTitle?: string | null;
}): Promise<{ id: number; manageToken: string }> {
  const c = publicClient();
  const { data, error } = await c.rpc("informate_vibe_site_create_guest", {
    p_title: params.title,
    p_url: params.url,
    p_description: params.description,
    p_category_id: params.categoryId,
    p_password: params.password,
    p_guest_display_name: params.guestDisplayName ?? null,
    p_tag_ids: params.tagIds,
    p_preview_image_url: params.previewImageUrl ?? null,
    p_preview_title: params.previewTitle ?? null,
  });
  if (error) throw error;
  const row = data as { id: number; manage_token: string };
  return { id: Number(row.id), manageToken: String(row.manage_token) };
}

export async function updateInformateVibeSiteAuth(params: {
  id: number;
  title: string;
  url: string;
  description: string;
  categoryId: number;
  tagIds: number[];
  previewImageUrl?: string | null;
  previewTitle?: string | null;
}): Promise<void> {
  const c = mainClient();
  const { error } = await c.rpc("informate_vibe_site_update", {
    p_id: params.id,
    p_title: params.title,
    p_url: params.url,
    p_description: params.description,
    p_category_id: params.categoryId,
    p_tag_ids: params.tagIds,
    p_preview_image_url: params.previewImageUrl ?? null,
    p_preview_title: params.previewTitle ?? null,
  });
  if (error) throw error;
}

export async function updateInformateVibeSiteGuest(params: {
  id: number;
  title: string;
  url: string;
  description: string;
  categoryId: number;
  tagIds: number[];
  previewImageUrl?: string | null;
  previewTitle?: string | null;
  password?: string;
  manageToken?: string;
}): Promise<void> {
  const c = publicClient();
  const { error } = await c.rpc("informate_vibe_site_update_guest", {
    p_id: params.id,
    p_title: params.title,
    p_url: params.url,
    p_description: params.description,
    p_category_id: params.categoryId,
    p_tag_ids: params.tagIds,
    p_preview_image_url: params.previewImageUrl ?? null,
    p_preview_title: params.previewTitle ?? null,
    p_password: params.password ?? null,
    p_manage_token: params.manageToken ?? null,
  });
  if (error) throw error;
}

export async function deleteInformateVibeSiteAuth(id: number): Promise<void> {
  await cleanupVibeSiteStorage(id);
  const c = mainClient();
  const { error } = await c.rpc("informate_vibe_site_delete", { p_id: id });
  if (error) throw error;
}

export async function deleteInformateVibeSiteGuest(
  id: number,
  opts: { password?: string; manageToken?: string }
): Promise<void> {
  // Prefer session client for storage cleanup when available
  await cleanupVibeSiteStorage(id);
  const c = publicClient();
  const { error } = await c.rpc("informate_vibe_site_delete_guest", {
    p_id: id,
    p_password: opts.password ?? null,
    p_manage_token: opts.manageToken ?? null,
  });
  if (error) throw error;
}

export async function toggleInformateVibeSiteLikeFromSupabase(
  siteId: number
): Promise<{ liked: boolean; likes_count: number }> {
  const c = mainClient();
  const { data, error } = await c.rpc("toggle_informate_vibe_site_like", { p_site_id: siteId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    liked: Boolean(row?.liked),
    likes_count: Number(row?.likes_count ?? 0),
  };
}

export async function listInformateVibeSiteCommentsFromSupabase(siteId: number): Promise<InformateVibeSiteComment[]> {
  // Session-aware: view column can_edit uses django_user_id()
  const c = mainClient();
  const { data, error } = await c
    .from("informate_vibe_site_comment_list_view")
    .select("*")
    .eq("site_id", siteId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: Number(r.id),
    site_id: Number(r.site_id),
    parent_id: r.parent_id == null ? null : Number(r.parent_id),
    author_user_id: Number(r.author_user_id),
    author_display_name: String(r.author_display_name ?? "회원"),
    author_avatar_path: r.author_avatar_path == null ? null : String(r.author_avatar_path),
    body: String(r.body ?? ""),
    is_deleted: Boolean(r.is_deleted),
    created_at: String(r.created_at ?? ""),
    updated_at: String(r.updated_at ?? ""),
    can_edit: Boolean(r.can_edit),
  }));
}

export async function createInformateVibeSiteComment(
  siteId: number,
  body: string,
  parentId?: number | null
): Promise<number> {
  const c = mainClient();
  const { data, error } = await c.rpc("informate_vibe_site_comment_create", {
    p_site_id: siteId,
    p_body: body,
    p_parent_id: parentId ?? null,
  });
  if (error) throw error;
  return Number(data);
}

export async function updateInformateVibeSiteComment(commentId: number, body: string): Promise<void> {
  const c = mainClient();
  const { error } = await c.rpc("informate_vibe_site_comment_update", {
    p_comment_id: commentId,
    p_body: body,
  });
  if (error) throw error;
}

export async function deleteInformateVibeSiteComment(commentId: number): Promise<void> {
  const c = mainClient();
  const { error } = await c.rpc("informate_vibe_site_comment_delete", { p_comment_id: commentId });
  if (error) throw error;
}

export async function setInformateVibeSiteHidden(siteId: number, hidden: boolean): Promise<void> {
  const c = mainClient();
  const { error } = await c.rpc("informate_vibe_site_set_hidden", {
    p_site_id: siteId,
    p_hidden: hidden,
  });
  if (error) throw error;
}

export async function isInformateVibeSuperuser(): Promise<boolean> {
  const uid = await getMyDjangoUserId();
  if (!uid || !supabase) return false;
  const { data } = await supabase.from("auth_user").select("is_superuser").eq("id", uid).maybeSingle();
  return Boolean(data?.is_superuser);
}

const pendingHits = new Map<number, Promise<number | null>>();
const viewedSites = new Set<number>();

/** Count successful detail visits once per tab session, including concurrent mounts. */
export function incrementInformateVibeSiteHit(siteId: number): Promise<number | null> {
  const pending = pendingHits.get(siteId);
  if (pending) return pending;
  const key = `informate_vibe_site_viewed:${siteId}`;
  if (viewedSites.has(siteId)) return Promise.resolve(null);
  try {
    if (sessionStorage.getItem(key)) return Promise.resolve(null);
  } catch { /* Storage can be unavailable in private browsing. */ }

  const request = (async () => {
    try {
      const { data, error } = await publicClient().rpc("increment_informate_vibe_site_hit", { p_site_id: siteId });
      if (error) throw error;
      if (data == null) return null;
      viewedSites.add(siteId);
      try { sessionStorage.setItem(key, "1"); } catch { /* Keep in-memory deduplication. */ }
      return Number(data);
    } catch (error) {
      console.error("Failed to count vibe site visit", error);
      return null;
    } finally {
      pendingHits.delete(siteId);
    }
  })();
  pendingHits.set(siteId, request);
  return request;
}
